import { useEffect, useRef, useState } from "react";

import { serverGetServerTime } from "../../api/api-server-data";
import { t } from "../../i18n/i18n";
import { createId } from "../../shared/utils";
import { retryOnNetworkError } from "../api";

interface TimeRemaining {
  timeRemainingMs: number | null;
  timeRemainingStr: string; // ex. "9:30"
  timeRemainingStrFull: string; // ex. "00:09:30"
  timeRemainingStrMd: string; // ex. "09:30" when < 1hr, "01:09:30" when >= 1hr
  timeLabel: string; // ex. "minutes"
}

// Global state object containing all time tracking state
const timeTrackingState = {
  globalInterval: null as NodeJS.Timeout | null,
  serverTimeOffsetMs: null as number | null,
  isSyncingServerTime: false,
  timeUpdateCallbacks: new Map<string, (currentTimeMs: number | null) => void>(),
};

const DEFAULT_TIME_REMAINING: TimeRemaining = {
  timeRemainingMs: null,
  timeRemainingStr: "0",
  timeRemainingStrFull: "00:00:00",
  timeRemainingStrMd: "00:00",
  timeLabel: "seconds",
};

// Export for testing purposes only
export const resetTimeTrackingState = (): void => {
  if (timeTrackingState.globalInterval) {
    clearInterval(timeTrackingState.globalInterval);
    timeTrackingState.globalInterval = null;
  }
  timeTrackingState.serverTimeOffsetMs = null;
  timeTrackingState.isSyncingServerTime = false;
  timeTrackingState.timeUpdateCallbacks.clear();

  // Restart the interval after reset
  startGlobalInterval();
};

// ============================================================================
// FORMATTING HELPERS
// ============================================================================

export const padWithZero = (num: number) => {
  return String(num).padStart(2, "0");
};

const formatTimeRemaining = (timeRemainingMs: number | null) => {
  const { hours, minutes, seconds } = convertTime(timeRemainingMs);

  // Format time remaining string (e.g., "9:30", "1:23:45")
  let timeRemainingStr: string;
  if (hours > 0) {
    timeRemainingStr = `${hours}:${padWithZero(minutes)}:${padWithZero(seconds)}`;
  } else if (minutes > 0) {
    timeRemainingStr = `${minutes}:${padWithZero(seconds)}`;
  } else {
    timeRemainingStr = `${seconds}`;
  }

  // Format full time string (e.g., "00:09:30")
  const timeRemainingStrFull = `${padWithZero(hours)}:${padWithZero(minutes)}:${padWithZero(seconds)}`;

  // Format medium time string: same as full when >= 1hr, else MM:SS when < 1hr (e.g., "05:00", "00:45")
  const timeRemainingStrMd =
    hours > 0 ? timeRemainingStrFull : `${padWithZero(minutes)}:${padWithZero(seconds)}`;

  // Get time label (e.g., "minutes", "hours")
  let timeLabel: string;
  if (hours > 0) {
    const isPlural = hours > 1 || (hours === 1 && (minutes > 0 || seconds > 0));
    timeLabel = t("common.time.hour", { count: isPlural ? 2 : 1 });
  } else if (minutes > 0) {
    const isPlural = minutes > 1 || (minutes === 1 && seconds > 0);
    timeLabel = t("common.time.minute", { count: isPlural ? 2 : 1 });
  } else {
    timeLabel = t("common.time.second", { count: seconds });
  }

  return { timeRemainingStr, timeRemainingStrFull, timeRemainingStrMd, timeLabel };
};

// ============================================================================
// TIME HELPERS
// ============================================================================

const convertTime = (timeRemainingMs: number | null) => {
  const totalSeconds = timeRemainingMs ? Math.floor(timeRemainingMs / 1000) : 0;
  const seconds = Math.floor(totalSeconds % 60);
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const hours = Math.floor(totalSeconds / (60 * 60));
  return { hours, minutes, seconds };
};

const getLocalTime = (): number => {
  return Date.now();
};

const getServerTimeOffset = (): number | null => {
  return timeTrackingState.serverTimeOffsetMs;
};

export const getCurrentServerTimeMs = (): number | null => {
  const offsetMs = getServerTimeOffset();
  if (offsetMs === null) return null;
  return getLocalTime() + offsetMs;
};

/**
 * Initializes the server time offset by fetching the current server time
 * and calculating the offset from local time.
 *
 * Steps:
 * 1. Take the server time we received (serverTimeMs)
 * 2. Add half the round-trip time to compensate for network latency (roundTripTimeMs / 2)
 * 3. Subtract the local time at request to get the offset
 *
 * Example: If server time was 1000ms when we requested it, roundTripTimeMs was 50ms,
 * and local time was 500ms when we made the request:
 * serverTimeOffsetMs = (1000 + 25) - 500 = 525ms
 * Later, when local time is 1500ms, current server time ≈ 1500 + 525 = 2025ms
 */
const syncServerTime = async (): Promise<void> => {
  if (getServerTimeOffset() !== null || timeTrackingState.isSyncingServerTime) return;

  timeTrackingState.isSyncingServerTime = true;

  try {
    let fetchStartTime = 0;
    const data = await retryOnNetworkError(
      async () => {
        fetchStartTime = performance.now();
        return serverGetServerTime();
      },
      "serverTime",
      3,
    );

    const fetchEndTime = performance.now();
    const roundTripTimeMs = fetchEndTime - fetchStartTime;

    const serverTimeMs = data.server_time;
    const localTimeMs = getLocalTime();
    const _serverTimeOffsetMs = serverTimeMs + roundTripTimeMs / 2.0 - localTimeMs;

    timeTrackingState.serverTimeOffsetMs = _serverTimeOffsetMs;
  } catch (error) {
    console.error("Failed to initialize server time:", error);
  } finally {
    timeTrackingState.isSyncingServerTime = false;
  }
};

/**
 * Starts the global interval that notifies all registered subscribers
 * with the current adjusted server time every second.
 */
const startGlobalInterval = (): void => {
  if (timeTrackingState.globalInterval) return;

  const intervalCallback = () => {
    if (timeTrackingState.timeUpdateCallbacks.size === 0) return;
    const currentServerTimeMs = getCurrentServerTimeMs();
    timeTrackingState.timeUpdateCallbacks.forEach((callback) => callback(currentServerTimeMs));
  };

  timeTrackingState.globalInterval = setInterval(intervalCallback, 1000);
};

/**
 * Returns the remaining time until a given end date.
 *
 * @param endDate - Target end date to count down to, or null to show zero time
 * @returns Remaining time in ms plus formatted strings and label
 */
export const useTimeRemaining = (endDate: Date | null): TimeRemaining => {
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>(DEFAULT_TIME_REMAINING);
  const subscriptionId = useRef<string | null>(null);
  const endTimeMs = endDate?.getTime() ?? null;

  useEffect(() => {
    if (getServerTimeOffset() === null) {
      syncServerTime();
    }

    if (!endTimeMs) {
      setTimeRemaining(DEFAULT_TIME_REMAINING);
      if (subscriptionId.current) {
        timeTrackingState.timeUpdateCallbacks.delete(subscriptionId.current);
      }
      return;
    }

    const callback = (currentServerTimeMs: number | null) => {
      if (currentServerTimeMs === null) {
        setTimeRemaining(DEFAULT_TIME_REMAINING);
        return;
      }

      const _timeRemainingMs = endTimeMs - currentServerTimeMs;
      const timeRemainingMs = _timeRemainingMs > 0 ? _timeRemainingMs : null;

      const { timeRemainingStr, timeRemainingStrFull, timeRemainingStrMd, timeLabel } =
        formatTimeRemaining(timeRemainingMs);

      setTimeRemaining({
        timeRemainingMs,
        timeRemainingStr,
        timeRemainingStrFull,
        timeRemainingStrMd,
        timeLabel,
      });
    };

    subscriptionId.current = createId();
    timeTrackingState.timeUpdateCallbacks.set(subscriptionId.current, callback);

    const currentServerTimeMs = getCurrentServerTimeMs();
    callback(currentServerTimeMs);

    return () => {
      if (subscriptionId.current) {
        timeTrackingState.timeUpdateCallbacks.delete(subscriptionId.current);
      }
    };
  }, [endTimeMs]);

  return timeRemaining;
};

if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    if (timeTrackingState.globalInterval) {
      clearInterval(timeTrackingState.globalInterval);
      timeTrackingState.globalInterval = null;
    }
  });
}

// Start the global interval immediately when module loads
startGlobalInterval();
